import {ContentUpvotesAPI} from 'api/content_upvotes';
import {DiscussionBoardAPI} from 'api/discussion_board';
import {TrackingAPI} from 'api/tracking';
import {ContributePostLink} from 'components/discussion_board/contribute_post_link';
import {ModeratePost} from 'components/discussion_board/moderate_post';
import {AsyncButton} from 'components/system/async_button';
import {BetweenDots} from 'components/system/between_dots';
import {PillTag} from 'components/system/pilltag';
import {PillTagGroup} from 'components/system/pilltag_group';
import {ProfileImage} from 'components/system/profile_image';
import {FormatShortDate} from 'components/system/timestamp';
import {Vote} from 'components/system/vote';
import {LockIcon} from 'icons/lock';
import Link from 'next/link';
import {useEffect, useState} from 'react';
import {useAuth, useUpsell} from 'users';
import styles from './post_card.module.css';

export const PostCard = ({post, showVote = true, showModeration = true, onDelete}: Props) => {
  const {user, hasPermission} = useAuth();
  const {showUpsellModal} = useUpsell();
  const [locked, setLocked] = useState(false);

  useEffect(() => {
    setLocked(!hasPermission('locked_post_view', post.slug));
  }, [user, post]);

  return (
    <div className={styles.container}>
      {showVote && (
        <Vote
          upvotes={post.upvotes}
          userVote={post.user_vote}
          onVote={(vote) => ContentUpvotesAPI.upvotePost(post.uuid, vote)}
          disabled={post.author_identifier === user.uuid}
          layout='vertical'
        />
      )}
      <div className={styles.content}>
        {showModeration && <ModeratePost post={post} />}
        <Link prefetch={false} href={`/discuss/${post.topic_slug}/posts/${post.slug}`}>
          <a
            onClick={(e) => {
              if (post.topic.is_locked && !hasPermission('locked_post_view', post.slug)) {
                e.preventDefault();
                showUpsellModal({
                  sourceObject: post,
                  cta: 'post.openLockedPost',
                  ctaParams: {topic: post.topic.name.toLowerCase()},
                  extraActions: <ContributePostLink post={post} />,
                });
              }
              TrackingAPI.discussionListModuleViewed({
                action: 'click',
                extra_data: {
                  topic: post.topic.name,
                  post_name: post.subject,
                  content_identifier: post.uuid,
                },
              });
            }}>
            <div className={styles.header}>
              {locked && <LockIcon />}
              {post.subject}
            </div>
            {post.body_preview && <p>{post.body_preview}</p>}
          </a>
        </Link>
        {post.tags.length > 0 && (
          <PillTagGroup>
            {post.tags.map((tag) => (
              <PillTag key={tag.keyword}>{tag.keyword}</PillTag>
            ))}
          </PillTagGroup>
        )}
        <BetweenDots>
          <ProfileImage
            displayName={post.author}
            url={post.author_img}
            isAnonymous={post.is_anonymous}
          />
          <strong>{post.is_anonymous ? 'Anonymous' : post.author}</strong>
          <span>
            <FormatShortDate>{post.created_at}</FormatShortDate>
          </span>
          {!showVote && (
            <span>
              {post.upvotes} upvote{post.upvotes === 1 ? '' : 's'}
            </span>
          )}
          <span>
            {post.comment_count} comment{post.comment_count === 1 ? '' : 's'}
          </span>
          {(hasPermission('post_admin') || post.author === user.username) && onDelete && (
            <AsyncButton
              type='text'
              color='greyDark'
              onRequest={async () => {
                await DiscussionBoardAPI.deletePost(post);
                hasPermission('post_admin') &&
                  TrackingAPI.modAction('delete_post', user, post.uuid, post);
              }}
              onSuccess={() => onDelete(post)}>
              Delete
            </AsyncButton>
          )}
        </BetweenDots>
      </div>
    </div>
  );
};

type Props = {
  post: Post;
  showVote?: boolean;
  showModeration?: boolean;
  onDelete?: (post: Post) => void;
};
